/**
 * Browser-side wrapper around @hrms/shared's `extractOrgSlugFromHost` — see that module's header
 * comment for why a hostname is a valid, equally-trusted way to name a tenant pre-auth.
 *
 * `VITE_APP_DOMAIN` is baked in at build time (see docs/DEPLOY.md's note on `VITE_API_URL`, the
 * same mechanism). Unset in dev, it falls back to `lvh.me` — the root `.env.example`'s `APP_DOMAIN`
 * default — so a fresh checkout gets working subdomains locally with no extra `.env` file.
 */

import { extractOrgSlugFromHost } from '@hrms/shared';

/** Exported so the login screen can show the real domain instead of a hardcoded placeholder. */
export const APP_DOMAIN = import.meta.env.VITE_APP_DOMAIN ?? 'lvh.me';

/** The current tenant's slug from `window.location.hostname`, or `null` on the bare app domain. */
export function currentOrgSlug(): string | null {
  return extractOrgSlugFromHost(window.location.hostname, APP_DOMAIN);
}

/**
 * True when the dev server is reached through a host outside APP_DOMAIN (a GitHub Codespaces
 * forwarded URL, a LAN IP): `<slug>.APP_DOMAIN` does not route back to this machine from there,
 * so a post-login subdomain handoff would strand the user signed out. Always false in a build.
 */
export function isDevOffAppDomain(): boolean {
  const host = window.location.hostname.toLowerCase();
  return import.meta.env.DEV && host !== APP_DOMAIN && !host.endsWith(`.${APP_DOMAIN}`);
}

/** The origin (scheme + host + port) a given tenant slug's subdomain is reachable at. */
export function orgSubdomainOrigin(slug: string): string {
  return `${window.location.protocol}//${slug}.${APP_DOMAIN}${window.location.port ? `:${window.location.port}` : ''}`;
}

/**
 * The origin with no tenant subdomain at all — `APP_DOMAIN` itself, not `<slug>.APP_DOMAIN`.
 *
 * Signing out is the one place this matters: a session belongs to one company's subdomain, but
 * "you are no longer signed in" is not a fact about any particular company, so it lands on the
 * neutral entry point rather than leaving the ex-session's own subdomain in the address bar.
 */
export function bareAppOrigin(): string {
  return `${window.location.protocol}//${APP_DOMAIN}${window.location.port ? `:${window.location.port}` : ''}`;
}

/**
 * A public job posting's shareable apply link — `/careers/:orgSlug/:postingSlug` (App.tsx). Built
 * on the tenant's OWN subdomain via `orgSubdomainOrigin`, not `window.location.origin`: the admin
 * area doesn't yet require being on the org's subdomain (production hasn't cut the apex over — see
 * ADR 0007's "Rollout"), so an admin working from the bare app domain would otherwise get a link
 * like `https://<appDomain>/careers/acme/...` with no company in it at all, instead of
 * `https://acme.<appDomain>/careers/acme/...`. The route also takes `orgSlug` from the path itself,
 * so the link keeps working regardless of which host serves the bundle.
 * `orgSlug` is the CURRENT admin session's org — this helper is only ever called from inside the
 * admin area, where `currentOrgSlug()`, when on a subdomain, agrees with it; on the bare domain we
 * fall back to the session's own org slug via `fallbackOrgSlug`.
 */
export function careersOrgUrl(postingSlug: string, fallbackOrgSlug?: string): string {
  const orgSlug = currentOrgSlug() ?? fallbackOrgSlug ?? '';
  return `${orgSubdomainOrigin(orgSlug)}/careers/${orgSlug}/${postingSlug}`;
}
